import { useQuery } from '@tanstack/react-query';
import type { Location } from '@t90/common/supabase';
import { useSupabase, queryKeys } from '../provider';

// =====================================================
// LOCATION QUERIES
// =====================================================

interface LocationFilters {
  active?: boolean;
}

export function useLocations(filters?: LocationFilters) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: queryKeys.locations.list(filters),
    queryFn: async (): Promise<Location[]> => {
      let query = supabase
        .from('locations')
        .select('*')
        .order('location_name');

      if (filters?.active !== undefined) {
        query = query.eq('active', filters.active);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Location[];
    },
  });
}

export function useLocation(locationId: string | undefined) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: queryKeys.locations.detail(locationId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select(`
          *,
          stations(*),
          team_leads(*, app_user:app_users(id, full_name)),
          trainees(*, app_user:app_users(id, full_name))
        `)
        .eq('id', locationId!)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!locationId && locationId !== 'new',
  });
}

// =====================================================
// STATION QUERIES
// =====================================================

export function useStations(locationId?: string) {
  const supabase = useSupabase();

  return useQuery({
    queryKey: ['stations', locationId],
    queryFn: async () => {
      let query = supabase
        .from('stations')
        .select(`
          *,
          location:locations(id, location_name, location_code)
        `)
        .eq('active', true)
        .order('station_name');

      if (locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });
}
